import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from 'react-bootstrap/Modal';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import PaymentAttachmentPopover from './PaymentAttachmentPopover';
import {
  defineOrderStatusClassName,
  getOrderStatusMainLabel,
  sortOrdersByMatchingPaymentAmount,
} from '../../../../utils/adminOrderService';
import { getOrderId } from './paymentAttachmentOrders';
import usePaymentAttachment from './usePaymentAttachment';
import '../../../../styles/payment-attachment-modal.css';

const formatCurrency = value =>
  `${Number(value ?? 0).toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} грн`;
const formatCompactAmount = value =>
  Number(value ?? 0).toLocaleString('uk-UA', { maximumFractionDigits: 2 });
const getCustomerName = order =>
  [order.userInfo?.surname, order.userInfo?.name].filter(Boolean).join(' ');

function OrderSummary({ order }) {
  return <>
    <div className='payment-attachment-order-heading'>
      <span className='payment-attachment-order-name'>
        <span className='payment-attachment-order-id'>#{getOrderId(order)}</span>
        {' '}{getCustomerName(order)}
      </span>
      {order.orderStatus && (
        <span className='payment-attachment-order-actions'>
          <span className={`${defineOrderStatusClassName(order.orderStatus)} payment-attachment-order-status`}>
            {getOrderStatusMainLabel(order.orderStatus)}
          </span>
        </span>
      )}
    </div>
    <span className='text-muted'>
      Сума: {formatCurrency(order.discountedPrice ?? order.price)}
    </span>
  </>;
}

function AttachmentResult({ message, description, warning }) {
  return <>
    <span className='payment-attachment-notice-icon' aria-hidden='true'>
      <i className='bi bi-check-lg' />
    </span>
    <div className='payment-attachment-notice-content'>
      <strong className='payment-attachment-notice-title'>{message}</strong>
      {description && <div className='payment-attachment-notice-description'>{description}</div>}
      {warning && <div className='payment-attachment-toast-warning'>{warning}</div>}
    </div>
  </>;
}

// Own the identity boundary so callers cannot accidentally reuse another payment's state.
export default function PaymentAttachmentModal(props) {
  const [notification, setNotification] = useState(null);
  const [noticeHovered, setNoticeHovered] = useState(false);
  const [noticeFocused, setNoticeFocused] = useState(false);
  useEffect(() => {
    if (!notification || notification.warning || noticeHovered || noticeFocused) return;
    const timeout = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timeout);
  }, [notification, noticeHovered, noticeFocused]);
  const showNotification = result => {
    setNoticeHovered(false);
    setNoticeFocused(false);
    setNotification(result);
  };
  return <>
    {props.payment && <PaymentAttachmentDialog key={props.payment.id} {...props} onComplete={showNotification} />}
    {notification && createPortal(
      <div className='payment-attachment-notice payment-attachment-toast' role='status' aria-live='polite'
        onMouseEnter={() => setNoticeHovered(true)} onMouseLeave={() => setNoticeHovered(false)}
        onFocusCapture={() => setNoticeFocused(true)}
        onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setNoticeFocused(false);
        }}>
        <AttachmentResult message={notification.message} description='Усю суму оплати розподілено.' warning={notification.warning} />
        <button type='button' className='close' aria-label='Закрити сповіщення' onClick={() => setNotification(null)}>
          <span aria-hidden='true'>×</span>
        </button>
      </div>, document.body,
    )}
  </>;
}

function PaymentAttachmentDialog({
  payment,
  onClose,
  onAttached,
  onComplete,
  presentation = 'modal',
  anchor,
  onBusyChange,
}) {
  const {
    details,
    search,
    orders,
    ordersLoading,
    ordersError,
    selectedOrder,
    saving,
    saveError,
    success,
    availableAmount,
    fullyUsed,
    canAttach,
    loadDetails,
    handleAttach,
    handleClose,
    clearSelection,
    retrySearch,
    changeSearch,
    selectOrder,
  } = usePaymentAttachment({ payment, onClose, onAttached, onComplete });
  useEffect(() => {
    onBusyChange?.(saving);
  }, [saving, onBusyChange]);
  const changeOrderRef = useRef(null);
  const searchInputRef = useRef(null);
  const closeButtonRef = useRef(null);
  useEffect(() => {
    if (saving) return;
    const target = fullyUsed ? closeButtonRef.current : selectedOrder ? changeOrderRef.current : searchInputRef.current;
    target?.focus();
  }, [fullyUsed, selectedOrder, saving]);
  const sortedOrders = sortOrdersByMatchingPaymentAmount(orders, availableAmount);
  const remainingAmount = Math.max(0, availableAmount);
  const totalAmount = Number(details.payment.amount ?? 0);
  const partiallyUsed = remainingAmount > 0 && remainingAmount < totalAmount;

  const content = (
    <form onSubmit={handleAttach} className='payment-attachment-form'>
      <div className='modal-header'>
        <div className='payment-attachment-heading'>
          <h5 className='modal-title' id='payment-attachment-title'>
            {fullyUsed ? 'Оплата' : 'Прикріпити оплату'} #{payment.id}
          </h5>
          {!fullyUsed && <span className='payment-attachment-amount' aria-live='polite' aria-busy={details.loading}>
            {details.error ? '—' : <>
              <OverlayTrigger placement='top' overlay={
                <Tooltip id={`payment-${payment.id}-available-amount`}>
                  <strong>Залишок оплати, доступний для прикріплення до замовлень.</strong>
                </Tooltip>
              }>
                <span tabIndex={0} className={`payment-attachment-amount-value${partiallyUsed ? ' payment-attachment-amount-available' : ''}`}>
                  <span className='sr-only'>Доступно: </span>
                  {formatCompactAmount(remainingAmount)}
                </span>
              </OverlayTrigger>
              {remainingAmount < totalAmount && <>
                <span className='payment-attachment-amount-separator' aria-hidden='true'>/</span>
                <OverlayTrigger placement='top' overlay={
                  <Tooltip id={`payment-${payment.id}-total-amount`}>
                    <strong>Повна сума оплати до прикріплення до замовлень.</strong>
                  </Tooltip>
                }>
                  <span tabIndex={0} className='payment-attachment-amount-value'>
                    <span className='sr-only'>Загальна сума оплати: </span>
                    {formatCompactAmount(totalAmount)}
                  </span>
                </OverlayTrigger>
              </>}
              <span>грн</span>
            </>}
          </span>}
          {details.attachments.length > 0 && (
            <ul className='payment-attachment-header-attachments' aria-label='Прикріплені замовлення'>
              {details.attachments.map(attachment => (
                <li key={attachment.id}>
                  <a className='payment-attachment-existing-row'
                    href={`/admin/order/${attachment.orderId}`} target='_blank' rel='noopener noreferrer'>
                    <i className='bi bi-paperclip' aria-hidden='true' />
                    <span className='payment-attachment-existing-id'>
                      <span className='sr-only'>Замовлення </span>#{attachment.orderId}
                    </span>
                    <span className='payment-attachment-existing-amount'>{formatCurrency(attachment.amount)}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type='button'
          className='close'
          ref={closeButtonRef}
          aria-label='Закрити'
          onClick={handleClose}
          disabled={saving}
        >
          <span aria-hidden='true'>×</span>
        </button>
      </div>
      {!fullyUsed && <div className='modal-body'>
        <div className='payment-attachment-section-heading payment-attachment-selection-heading'>
          <h6 className='payment-attachment-section-title'>
            {selectedOrder ? 'Вибране замовлення' : 'Вибрати замовлення'}
          </h6>
          {selectedOrder && (
            <button
              type='button'
              className='payment-attachment-change'
              aria-label='Змінити замовлення'
              title='Змінити замовлення'
              onClick={clearSelection}
              ref={changeOrderRef}
              disabled={saving}
            >
              <i className='bi bi-pencil' aria-hidden='true' />
            </button>
          )}
        </div>
        {selectedOrder ? (
          <div className='payment-attachment-selected'>
            <OrderSummary order={selectedOrder} />
          </div>
        ) : (
          <div className='payment-attachment-picker'>
            <input
              id='payment-attachment-search'
              ref={searchInputRef}
              className='form-control'
              type='search'
              aria-label='ID замовлення, прізвище або сума'
              autoFocus
              autoComplete='off'
              placeholder='ID замовлення, прізвище або сума'
              value={search}
              disabled={saving}
              onChange={event => changeSearch(event.target.value)}
            />
            <div
              className='payment-attachment-orders'
              aria-label='Доступні замовлення'
              aria-busy={ordersLoading || details.loading}
            >
              {ordersLoading || details.loading ? (
                <div className='payment-attachment-orders-loading' role='status'>
                  <span className='sr-only'>Завантаження замовлень…</span>
                  {Array.from({ length: 6 }, (_, index) => (
                    <div className='payment-attachment-skeleton-row' aria-hidden='true' key={index}>
                      <span className='payment-attachment-skeleton-name' />
                      <span className='payment-attachment-skeleton-status' />
                      <span className='payment-attachment-skeleton-price' />
                    </div>
                  ))}
                </div>
              ) : ordersError ? (
                <div className='p-3' role='alert'>
                  {ordersError}
                  <button
                    type='button'
                    className='btn btn-outline-secondary btn-sm ml-2'
                    onClick={retrySearch}
                  >
                    Повторити
                  </button>
                </div>
              ) : sortedOrders.length ? (
                sortedOrders.map(order => (
                  <button
                    type='button'
                    key={getOrderId(order)}
                    className='payment-attachment-order'
                    disabled={saving}
                    onClick={() => selectOrder(order)}
                  >
                    <OrderSummary order={order} />
                  </button>
                ))
              ) : (
                <div className='p-3 text-muted'>
                  {search.trim()
                    ? 'Нічого не знайдено'
                    : 'Немає доступних замовлень з оплатою карткою'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>}
      <div className='modal-footer'>
        {(success || saveError || details.error || (fullyUsed && details.loading) || (!details.loading && availableAmount <= 0)) && (
          <div className='payment-attachment-feedback'>
            {fullyUsed && details.loading && (
              <div role='status'>
                <span className='spinner-border spinner-border-sm text-muted' aria-hidden='true' />
                <span className='sr-only'>Оновлення даних оплати…</span>
              </div>
            )}
            {success && (
              <div className='payment-attachment-notice' role='status'>
                <AttachmentResult message={success} description={
                  !details.error && Number.isFinite(remainingAmount) && remainingAmount > 0
                    ? <>Доступно ще <strong>{formatCompactAmount(remainingAmount)} грн</strong></>
                    : null
                } />
              </div>
            )}
            {details.error && (
              <div className='alert alert-danger' role='alert'>
                {details.error}
                <button type='button' className='btn btn-outline-danger btn-sm ml-2'
                  onClick={loadDetails} disabled={details.loading || saving}>
                  Повторити
                </button>
              </div>
            )}
            {saveError && <div className='alert alert-danger' role='alert'>{saveError}</div>}
            {!details.loading && !details.error && availableAmount <= 0 && (
              <div className='alert alert-info' role='status'>Оплату вже повністю використано.</div>
            )}
          </div>
        )}
        {!fullyUsed && <button type='submit' className='btn btn-dark' disabled={!canAttach}>
          {saving ? 'Прикріплення…' : 'Прикріпити оплату'}
        </button>}
      </div>
    </form>
  );
  if (presentation === 'popover')
    return (
      <PaymentAttachmentPopover
        anchor={anchor}
        anchorId={`payment-attachment-trigger-${payment.id}`}
        onClose={handleClose}
      >
        {content}
      </PaymentAttachmentPopover>
    );
  return (
    <Modal
      show
      onHide={handleClose}
      size='lg'
      centered
      animation={false}
      keyboard={!saving}
      backdrop={saving ? 'static' : true}
      dialogClassName='payment-attachment-dialog'
      aria-labelledby='payment-attachment-title'
    >
      {content}
    </Modal>
  );
}
